import { useCallback, useMemo, useState } from 'react';
import { Field, FieldInputProps } from 'react-final-form';
import Label from 'koddi-components/Label';
import OnOutsideClick from 'koddi-components/OnOutsideClick';
import { Portal } from 'react-portal';
import { useKoddiTheme } from 'koddi-components/ThemeProvider';
import { format } from 'date-fns-tz';
import {
    addDays,
    isWithinInterval,
    parseISO,
    startOfDay,
    subDays,
} from 'date-fns';
import { faCalendar, faCircleCheck } from '@fortawesome/pro-regular-svg-icons';
import { CFAIcon } from 'app/koddi-components/src/_ChakraComponents/Icons';
import {
    DateRangePickerProps,
    DateRangePickerFieldProps,
    DateRangePickerFieldComponentProps,
    DateRangePickerCompareType,
} from './DateRangePicker.types';

import DateRangePickerList from './DateRangePickerList';
// eslint-disable-next-line import/no-cycle
import DateRangeButton from './DateRangeButton';
import DateRangeCalendars from './DateRangeCalendars';
import {
    DateRangePickerHookReturn,
    useDateRangePicker,
} from './DateRangePicker.hooks';
import DateRangePickerFooter from './DateRangePickerFooter';

import * as Styled from './DateRangePicker.styled';

import {
    CBoldText,
    CButtonGroup,
    CChevronButton,
    CDivider,
    Colors,
    COutlineButton,
    CRow,
    CSecondaryButton,
    CSelectField,
    CText,
    CTextInput,
    FontSizes,
    Spacing,
    Typography,
} from '../_ChakraComponents';
import { useChakraTheme } from '../_ChakraComponents/ChakraTheme/useChakraTheme.hooks';

const DateContent = ({
    dropdownOpen,
    selectedPresetDateRange,
    presetDateRanges,
    handlePresetRangeSelected,
    datesMap,
    months,
    hoveredDate,
    setHoveredDate,
    handleDatesRangeChange,
    rangeIsValid,
    disallowFutureDates,
    handleFirstMonthChange,
    handleSecondMonthChange,
    handleApply,
    handleCancel,
    error,
    name,
    inputProps,
    startDateId,
    endDateId,
    v2,
    disabledDates,
}: Pick<
    DateRangePickerHookReturn,
    | 'dropdownOpen'
    | 'selectedPresetDateRange'
    | 'presetDateRanges'
    | 'handlePresetRangeSelected'
    | 'datesMap'
    | 'months'
    | 'hoveredDate'
    | 'setHoveredDate'
    | 'handleDatesRangeChange'
    | 'rangeIsValid'
    | 'handleFirstMonthChange'
    | 'handleSecondMonthChange'
    | 'handleApply'
    | 'error'
> & {
    v2?: boolean;
    name: string;
    disallowFutureDates: boolean;
    inputProps: FieldInputProps<string, HTMLInputElement>['input'];
    startDateId?: string;
    endDateId?: string;
    handleCancel?: () => void;
    disabledDates?: [string, string][];
}) => {
    return (
        <Styled.DateRangePickerDropdown
            role="dialog"
            dropdownOpen={dropdownOpen}
        >
            <DateRangePickerList
                selectedRange={selectedPresetDateRange}
                ranges={presetDateRanges}
                onRangeSelected={handlePresetRangeSelected}
            />
            <Styled.DateRangePickerContent>
                <DateRangeCalendars
                    {...datesMap}
                    {...months}
                    startDateId={startDateId || `${name}-start-date`}
                    endDateId={endDateId || `${name}-end-date`}
                    onDayHover={setHoveredDate}
                    hoveredDate={hoveredDate}
                    onFirstMonthDateChange={handleFirstMonthChange}
                    onSecondMonthDateChange={handleSecondMonthChange}
                    onChange={handleDatesRangeChange}
                    rangeIsValid={rangeIsValid}
                    disallowFutureDates={disallowFutureDates}
                    v2={v2}
                    disabledDates={disabledDates}
                />
                <DateRangePickerFooter
                    onApply={handleApply}
                    onCancel={handleCancel}
                    error={error}
                    inputName={name}
                />
            </Styled.DateRangePickerContent>
            <Styled.DateRangePickerInput {...inputProps} readOnly />
        </Styled.DateRangePickerDropdown>
    );
};

export const ContinuousDateRangePicker = ({
    startDate = new Date(),
    endDate = new Date(),
    startDateId,
    endDateId,
    onDatesChange,
    allTimeDate,
    name,
    input = {
        name,
        value: '',
        onBlur: () => undefined,
        onChange: () => undefined,
        onFocus: () => undefined,
    },
    onApply = () => undefined,
    open = false,
    disallowFutureDates = false,
    v2,
    btnHeight,
    locale = 'en-US',
    disabled = false,
    chakraButton,
    placement = 'bottom-start',
    isContinuousMode = false,
    setIsContinuousMode,
}: DateRangePickerProps & {
    isContinuousMode?: boolean;
    setIsContinuousMode: React.Dispatch<React.SetStateAction<boolean>>;
}): JSX.Element => {
    const { onChange, ...inputProps } = input;
    const { zIndex } = useKoddiTheme();

    const {
        rootRef,
        setPopperRef,
        setRootRef,
        styles,
        attributes,
        dropdownOpen,
        datesMap,
        months,
        hoveredDate,
        setHoveredDate,
        handleDatesRangeChange,
        rangeIsValid,
        handleFirstMonthChange,
        handleSecondMonthChange,
        toggleDropdown,
        error,
        setDropdownOpen,
        setDates,
    } = useDateRangePicker({
        startDate,
        endDate,
        initialDateRangePreset: 'Custom',
        onDatesChange,
        allTimeDate,
        locale,
        onApply,
        open,
        onChange,
        placement,
    });

    const handleContinuous = useCallback(() => {
        setIsContinuousMode(!isContinuousMode);
        onDatesChange?.({
            startDate: datesMap.startDate,
            endDate: null,
        });
        setDates({
            startDate: datesMap.startDate,
            endDate: null,
        });
    }, [
        setIsContinuousMode,
        isContinuousMode,
        onDatesChange,
        datesMap.startDate,
        setDates,
    ]);

    return (
        <Styled.DateRangePickerWrapper
            marginRight={chakraButton ? 0 : 8}
            ref={setRootRef}
        >
            <COutlineButton
                testId="continuous-date-range-button"
                height={btnHeight}
                onClick={toggleDropdown}
                disabled={disabled}
            >
                <CFAIcon icon={faCalendar} mr={Spacing.XXXS} />
                <CText
                    text={`
                ${format(datesMap.startDate ?? new Date(), 'LLL dd, yyyy')}
                ${
                    !isContinuousMode
                        ? ` - ${format(
                              datesMap.endDate ?? new Date(),
                              'LLL dd, yyyy'
                          )}`
                        : ''
                }`}
                />
            </COutlineButton>

            <Portal>
                <OnOutsideClick
                    listenForOutsideClick={dropdownOpen}
                    onOutsideClick={() => setDropdownOpen(false)}
                    getRef={setPopperRef}
                    style={{
                        ...styles.popper,
                        zIndex: zIndex.dropdown,
                    }}
                    additionalElement={rootRef}
                    {...attributes.popper}
                >
                    <Styled.DateRangePickerDropdown
                        role="dialog"
                        dropdownOpen={dropdownOpen}
                    >
                        <Styled.DateRangePickerContent>
                            <DateRangeCalendars
                                {...datesMap}
                                {...months}
                                startDateId={
                                    startDateId || `${name}-start-date`
                                }
                                endDateId={endDateId || `${name}-end-date`}
                                onDayHover={setHoveredDate}
                                hoveredDate={hoveredDate}
                                onFirstMonthDateChange={handleFirstMonthChange}
                                onSecondMonthDateChange={
                                    handleSecondMonthChange
                                }
                                onChange={handleDatesRangeChange}
                                rangeIsValid={rangeIsValid}
                                disallowFutureDates={disallowFutureDates}
                                v2={v2}
                            />
                            <CDivider mt={Spacing.SM} mb={0} />

                            <CSecondaryButton
                                testId="make-continuous-button"
                                onClick={handleContinuous}
                                isDisabled={!datesMap.startDate}
                                gap={Spacing.XS}
                            >
                                <CFAIcon
                                    icon={faCircleCheck}
                                    color={
                                        isContinuousMode
                                            ? Colors.success
                                            : Colors.gray[500]
                                    }
                                />
                                <CText text="Continuous" />
                            </CSecondaryButton>
                            {error && (
                                <CBoldText
                                    color={Colors.error}
                                    fontSize={FontSizes.sm}
                                    mt={Spacing.XS}
                                    px={Spacing.SM}
                                >
                                    {error}
                                </CBoldText>
                            )}
                        </Styled.DateRangePickerContent>
                        <Styled.DateRangePickerInput {...inputProps} readOnly />
                    </Styled.DateRangePickerDropdown>
                </OnOutsideClick>
            </Portal>
        </Styled.DateRangePickerWrapper>
    );
};

/** The `Koddi` Date Range Picker */
export const DateRangePicker = ({
    startDate = new Date(),
    endDate = null,
    initialDateRangePreset,
    startDateId,
    endDateId,
    onDatesChange,
    allTimeDate,
    name,
    btnStyle,
    label,
    input = {
        name,
        value: '',
        onBlur: () => undefined,
        onChange: () => undefined,
        onFocus: () => undefined,
    },
    onApply = () => undefined,
    open = false,
    disallowFutureDates = false,
    v2,
    btnHeight,
    locale = 'en-US',
    disabled = false,
    chakraButton,
    disabledDates = [],
}: DateRangePickerProps): JSX.Element => {
    const { onChange, ...inputProps } = input;
    const { zIndex } = useKoddiTheme();

    // it works but its off by 1 day
    if (disabledDates.length > 0) {
        while (
            // eslint-disable-next-line no-loop-func
            disabledDates.some(([start, end]) => {
                return isWithinInterval(startOfDay(startDate ?? new Date()), {
                    start: parseISO(start),
                    end: parseISO(end),
                });
            })
        ) {
            startDate = addDays(startDate ?? new Date(), 1);
        }
    }

    const {
        rootRef,
        setPopperRef,
        setRootRef,
        styles,
        attributes,
        dropdownOpen,
        selectedPresetDateRange,
        datesMap,
        handleCancel,
        toggleDropdown,
        ...rest
    } = useDateRangePicker({
        startDate,
        endDate,
        initialDateRangePreset,
        onDatesChange,
        allTimeDate,
        locale,
        onApply,
        open,
        onChange,
    });

    return (
        <Styled.DateRangePickerWrapper
            marginRight={chakraButton ? 0 : 8}
            ref={setRootRef}
        >
            {label && <Label htmlFor={input.name}>{label}</Label>}
            <DateRangeButton
                btnStyle={btnStyle}
                height={btnHeight}
                presetDateRange={selectedPresetDateRange}
                startDate={datesMap.startDate}
                endDate={datesMap.endDate}
                toggleDropdown={toggleDropdown}
                dropdownOpen={dropdownOpen}
                disabled={disabled}
                chakraButton={chakraButton}
            />
            <Portal>
                <OnOutsideClick
                    listenForOutsideClick={dropdownOpen}
                    onOutsideClick={() => handleCancel()}
                    getRef={setPopperRef}
                    style={{
                        ...styles.popper,
                        zIndex: zIndex.dropdown,
                    }}
                    additionalElement={rootRef}
                    {...attributes.popper}
                >
                    <DateContent
                        {...rest}
                        dropdownOpen={dropdownOpen}
                        selectedPresetDateRange={selectedPresetDateRange}
                        datesMap={datesMap}
                        handleCancel={handleCancel}
                        inputProps={inputProps}
                        name={name}
                        disallowFutureDates={disallowFutureDates}
                        startDateId={startDateId}
                        endDateId={endDateId}
                        v2={v2}
                        disabledDates={disabledDates}
                    />
                </OnOutsideClick>
            </Portal>
        </Styled.DateRangePickerWrapper>
    );
};

export const SplitDateRangePicker = ({
    startDate = new Date(),
    endDate = null,
    initialDateRangePreset,
    startDateId,
    endDateId,
    onDatesChange,
    allTimeDate,
    name,
    input = {
        name,
        value: '',
        onBlur: () => undefined,
        onChange: () => undefined,
        onFocus: () => undefined,
    },
    onApply = () => undefined,
    open = false,
    disallowFutureDates = false,
    v2,
    btnHeight,
    locale = 'en-US',
    disabled = false,
    chakraButton,
}: DateRangePickerProps): JSX.Element => {
    const { onChange, ...inputProps } = input;
    const { zIndex } = useKoddiTheme();

    const {
        rootRef,
        setPopperRef,
        setRootRef,
        styles,
        attributes,
        dropdownOpen,
        selectedPresetDateRange,
        datesMap,
        handleCancel,
        toggleDropdown,
        dates,
        ...rest
    } = useDateRangePicker({
        startDate,
        endDate,
        initialDateRangePreset,
        onDatesChange,
        allTimeDate,
        locale,
        onApply,
        open,
        onChange,
    });

    return (
        <Styled.DateRangePickerWrapper
            marginRight={chakraButton ? 0 : 8}
            ref={setRootRef}
        >
            <CButtonGroup isAttached>
                <COutlineButton
                    testId="split-date-range-button"
                    height="30px"
                    maxWidth="220px"
                    minWidth="220px"
                    bg={Colors.white}
                    color={Colors.black}
                    cursor="default"
                    fontWeight={Typography.fontWeights.semibold}
                >
                    {`${format(
                        new Date(`${dates.startDate}`) ?? new Date(),
                        'LLL dd, yyyy'
                    )} - ${format(
                        dates.endDate ?? new Date(),
                        'LLL dd, yyyy'
                    )}`}
                </COutlineButton>
                <DateRangeButton
                    btnStyle="secondary"
                    height={btnHeight}
                    presetDateRange={selectedPresetDateRange}
                    startDate={datesMap.startDate}
                    endDate={datesMap.endDate}
                    toggleDropdown={toggleDropdown}
                    dropdownOpen={dropdownOpen}
                    disabled={disabled}
                    chakraButton={chakraButton}
                    maxWidth="135px"
                    minWidth="135px"
                />
            </CButtonGroup>
            <Portal>
                <OnOutsideClick
                    listenForOutsideClick={dropdownOpen}
                    onOutsideClick={() => handleCancel()}
                    getRef={setPopperRef}
                    style={{
                        ...styles.popper,
                        zIndex: zIndex.dropdown,
                    }}
                    additionalElement={rootRef}
                    {...attributes.popper}
                >
                    <DateContent
                        {...rest}
                        dropdownOpen={dropdownOpen}
                        selectedPresetDateRange={selectedPresetDateRange}
                        datesMap={datesMap}
                        handleCancel={handleCancel}
                        inputProps={inputProps}
                        name={name}
                        disallowFutureDates={disallowFutureDates}
                        startDateId={startDateId}
                        endDateId={endDateId}
                        v2={v2}
                    />
                </OnOutsideClick>
            </Portal>
        </Styled.DateRangePickerWrapper>
    );
};

export const MultiRangeDatePicker = ({
    initialDateRange,
    initialDateRangePreset,
    startDateId,
    endDateId,
    onDatesChange,
    allTimeDate,
    name,
    input = {
        name,
        value: '',
        onBlur: () => undefined,
        onChange: () => undefined,
        onFocus: () => undefined,
    },
    onApply = () => undefined,
    open = false,
    disallowFutureDates = false,
    locale = 'en-US',
    disabled = false,
    chakraButton,
    compareOpen = false,
    placement = 'bottom-start',
    allowPrevious = true,
    initialCompareType = 'week',
}: Omit<DateRangePickerProps, 'startDate' | 'endDate'> & {
    initialDateRange: {
        startDate: Date;
        endDate: Date;
        previousStartDate: Date | null;
        previousEndDate: Date | null;
    };
    initialCompareType?: DateRangePickerCompareType;
    allowPrevious?: boolean;
    compareOpen?: boolean;
}): JSX.Element => {
    const { onChange, ...inputProps } = input;
    const { zIndex } = useKoddiTheme();
    const [toggleCompare, setToggleCompare] = useState(compareOpen);
    const [initialized, setInitialized] = useState(false);
    const [isCurrent, setIsCurrent] = useState(true);
    const [dateRange, setDateRange] = useState<{
        startDate: Date | null;
        endDate: Date | null;
        previousStartDate: Date | null;
        previousEndDate: Date | null;
    }>(initialDateRange);
    const [selectedComparison, setSelectedComparison] = useState<
        DateRangePickerCompareType
    >(initialCompareType);
    const theme = useChakraTheme();

    const formatDateRange = (date1: Date | null, date2: Date | null) => {
        let str = '';
        if (date1) {
            str = date1 ? format(date1, 'MM/dd/yy') : '';
        }
        if (date2) {
            str = `${str} - ${date2 ? format(date2, 'MM/dd/yy') : ''}`;
        }
        return str;
    };

    const currentFormattedRange = useMemo(() => {
        return formatDateRange(dateRange.startDate, dateRange.endDate);
    }, [dateRange.endDate, dateRange.startDate]);
    const previousFormattedRange = useMemo(() => {
        return formatDateRange(
            dateRange.previousStartDate ??
                subDays(new Date(dateRange.startDate ?? new Date()), 7),
            dateRange.previousEndDate ??
                subDays(new Date(dateRange.endDate ?? new Date()), 7)
        );
    }, [
        dateRange.endDate,
        dateRange.previousEndDate,
        dateRange.previousStartDate,
        dateRange.startDate,
    ]);

    const onPreviousDatesChange = ({
        startDate,
        endDate,
    }: {
        startDate: Date | null;
        endDate: Date | null;
    }) => {
        setInitialized(true);
        setSelectedComparison('custom');
        setDateRange((prev) => ({
            ...prev,
            previousStartDate: startDate,
            previousEndDate: endDate,
        }));
    };
    const onCurrentDatesChange = ({
        startDate,
        endDate,
    }: {
        startDate: Date | null;
        endDate: Date | null;
    }) => {
        setDateRange((prev) => ({
            ...prev,
            startDate,
            endDate,
        }));
    };

    const currentDateRangePickerProps = useDateRangePicker({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        initialDateRangePreset: null,
        onDatesChange: onCurrentDatesChange,
        allTimeDate,
        locale,
        onApply,
        open,
        onChange,
        placement,
    });

    const previousDateRangePickerProps = useDateRangePicker({
        startDate: dateRange.previousStartDate,
        endDate: dateRange.previousEndDate,
        initialDateRangePreset: null,
        onDatesChange: onPreviousDatesChange,
        allTimeDate,
        locale,
        onApply,
        open,
        onChange,
        placement,
    });

    const applyDates = () => {
        onApply({
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            previousStartDate: toggleCompare
                ? dateRange.previousStartDate
                : null,
            previousEndDate: toggleCompare ? dateRange.previousEndDate : null,
            buttonLabel: initialDateRangePreset,
            compareOpen: toggleCompare,
        });
        currentDateRangePickerProps.setDropdownOpen(false);
    };

    const calculateComparisonDates = (
        value: string | undefined,
        dates: { startDate: Date | null; endDate: Date | null }
    ) => {
        const daysMap = {
            day: 1,
            week: 7,
            month: 30,
            default: 7,
        };

        const days = daysMap[value as keyof typeof daysMap] || daysMap.default;

        return {
            previousStartDate: subDays(dates.startDate ?? new Date(), days),
            previousEndDate: subDays(dates.endDate ?? new Date(), days),
        };
    };

    const handleCompareInputChange = (value?: string) => {
        const newValue = value as DateRangePickerCompareType;
        setSelectedComparison(newValue);
        setDateRange((prev) => ({
            ...prev,
            ...calculateComparisonDates(value, prev),
        }));
    };

    const handleToggleCompare = () => {
        if (toggleCompare) {
            setIsCurrent(true);
            setDateRange((prev) => ({
                ...prev,
                previousStartDate: null,
                previousEndDate: null,
            }));
        }
        const { previousStartDate, previousEndDate } = calculateComparisonDates(
            selectedComparison,
            {
                startDate: dateRange.startDate,
                endDate: dateRange.endDate,
            }
        );
        setDateRange((prev) => ({
            ...prev,
            previousStartDate: initialized
                ? dateRange.previousStartDate
                : previousStartDate,
            previousEndDate: initialized
                ? dateRange.previousEndDate
                : previousEndDate,
        }));
        setToggleCompare(!toggleCompare);
    };

    return (
        <Styled.DateRangePickerWrapper
            marginRight={chakraButton ? 0 : 8}
            ref={currentDateRangePickerProps.setRootRef}
        >
            <CChevronButton
                chevronDirection={
                    currentDateRangePickerProps.dropdownOpen ? 'up' : 'down'
                }
                onClick={currentDateRangePickerProps.toggleDropdown}
                testId="multi-date-range-dropdown-button"
                disabled={disabled}
                leftIcon={<CFAIcon icon={faCalendar} />}
            >
                {`${currentFormattedRange}
                    ${
                        previousFormattedRange.length > 0 && toggleCompare
                            ? `   &    ${previousFormattedRange}`
                            : ''
                    }`}
            </CChevronButton>
            <Portal>
                <OnOutsideClick
                    listenForOutsideClick={
                        currentDateRangePickerProps.dropdownOpen
                    }
                    onOutsideClick={() => {
                        setToggleCompare(compareOpen);
                        currentDateRangePickerProps.setDropdownOpen(false);
                        onDatesChange?.({
                            startDate:
                                currentDateRangePickerProps.restoreDates
                                    .startDate,
                            endDate:
                                currentDateRangePickerProps.restoreDates
                                    .endDate,
                            previousStartDate:
                                previousDateRangePickerProps.restoreDates
                                    .startDate,
                            previousEndDate:
                                previousDateRangePickerProps.restoreDates
                                    .endDate,
                            buttonLabel: initialDateRangePreset,
                        });
                        setDateRange({
                            startDate:
                                currentDateRangePickerProps.restoreDates
                                    .startDate,
                            endDate:
                                currentDateRangePickerProps.restoreDates
                                    .endDate,
                            previousStartDate:
                                previousDateRangePickerProps.restoreDates
                                    .startDate,
                            previousEndDate:
                                previousDateRangePickerProps.restoreDates
                                    .endDate,
                        });
                    }}
                    getRef={currentDateRangePickerProps.setPopperRef}
                    style={{
                        ...currentDateRangePickerProps.styles.popper,
                        zIndex: zIndex.dropdown,
                    }}
                    additionalElement={currentDateRangePickerProps.rootRef}
                    {...currentDateRangePickerProps.attributes.popper}
                >
                    <Styled.MultiDateRangePickerWrapper
                        dropdownOpen={currentDateRangePickerProps.dropdownOpen}
                    >
                        {allowPrevious && (
                            <CRow
                                alignItems="center"
                                border="1px solid"
                                borderColor={Colors.grayLight}
                                gap={Spacing.XS}
                            >
                                <CSecondaryButton
                                    background={Colors.grayLightest}
                                    testId="toggle-compare-button"
                                    minWidth="150px"
                                    height="42px"
                                    borderRadius="0px"
                                    border="none"
                                    borderRight="1px solid"
                                    borderColor={Colors.grayLight}
                                    onClick={handleToggleCompare}
                                    fontSize={Typography.fontSizes.sm}
                                >
                                    {toggleCompare
                                        ? 'Hide Compare Dates'
                                        : 'Compare Dates'}
                                </CSecondaryButton>
                                <CRow
                                    py={Spacing.XXS}
                                    pr={Spacing.SM}
                                    alignItems="center"
                                >
                                    <CTextInput
                                        value={currentFormattedRange}
                                        onChange={() => 1}
                                        name="date1"
                                        testId="date1"
                                        hideError
                                        maxW="150px"
                                        onClick={() => setIsCurrent(true)}
                                        style={
                                            toggleCompare
                                                ? {
                                                      borderColor: isCurrent
                                                          ? theme.colors
                                                                .primary['100']
                                                          : theme.colors
                                                                .grayLight,
                                                  }
                                                : {}
                                        }
                                    />
                                    {toggleCompare && (
                                        <>
                                            <CSelectField
                                                isControlledInput
                                                placeholder="Compare To"
                                                name="compare-to"
                                                maxWidth="150px"
                                                testId="date-compare"
                                                onChange={
                                                    handleCompareInputChange
                                                }
                                                inputValue={selectedComparison}
                                                options={[
                                                    {
                                                        label: 'Prev Day',
                                                        value: 'day',
                                                    },
                                                    {
                                                        label: 'Prev 7 Days',
                                                        value: 'week',
                                                    },
                                                    {
                                                        label: 'Prev 30 Days',
                                                        value: 'month',
                                                    },
                                                    {
                                                        label: 'Custom',
                                                        value: 'custom',
                                                    }, // this button literally does nothing but product wants it
                                                ]}
                                                hideError
                                            />
                                            <CTextInput
                                                maxW="150px"
                                                value={previousFormattedRange}
                                                name="date2"
                                                testId="date2"
                                                hideError
                                                onChange={() => 1}
                                                onClick={() =>
                                                    setIsCurrent(false)
                                                }
                                                style={{
                                                    borderColor: !isCurrent
                                                        ? theme.colors.primary[
                                                              '100'
                                                          ]
                                                        : theme.colors
                                                              .grayLight,
                                                }}
                                            />
                                        </>
                                    )}
                                </CRow>
                            </CRow>
                        )}
                        {isCurrent ? (
                            <DateContent
                                dropdownOpen={
                                    currentDateRangePickerProps.dropdownOpen
                                }
                                selectedPresetDateRange={
                                    currentDateRangePickerProps.selectedPresetDateRange
                                }
                                datesMap={currentDateRangePickerProps.datesMap}
                                handleDatesRangeChange={
                                    currentDateRangePickerProps.handleDatesRangeChange
                                }
                                inputProps={inputProps}
                                name={name}
                                disallowFutureDates={disallowFutureDates}
                                startDateId={startDateId}
                                endDateId={endDateId}
                                v2
                                presetDateRanges={
                                    currentDateRangePickerProps.presetDateRanges
                                }
                                handlePresetRangeSelected={
                                    currentDateRangePickerProps.handlePresetRangeSelected
                                }
                                months={currentDateRangePickerProps.months}
                                hoveredDate={
                                    currentDateRangePickerProps.hoveredDate
                                }
                                error={currentDateRangePickerProps.error}
                                setHoveredDate={
                                    currentDateRangePickerProps.setHoveredDate
                                }
                                rangeIsValid={
                                    currentDateRangePickerProps.rangeIsValid
                                }
                                handleFirstMonthChange={
                                    currentDateRangePickerProps.handleFirstMonthChange
                                }
                                handleSecondMonthChange={
                                    currentDateRangePickerProps.handleSecondMonthChange
                                }
                                handleApply={applyDates}
                            />
                        ) : (
                            <DateContent
                                dropdownOpen={
                                    currentDateRangePickerProps.dropdownOpen
                                }
                                selectedPresetDateRange={
                                    previousDateRangePickerProps.selectedPresetDateRange
                                }
                                datesMap={previousDateRangePickerProps.datesMap}
                                handleDatesRangeChange={
                                    previousDateRangePickerProps.handleDatesRangeChange
                                }
                                inputProps={inputProps}
                                name={name}
                                disallowFutureDates={disallowFutureDates}
                                startDateId={startDateId}
                                endDateId={endDateId}
                                v2
                                presetDateRanges={
                                    previousDateRangePickerProps.presetDateRanges
                                }
                                handlePresetRangeSelected={
                                    previousDateRangePickerProps.handlePresetRangeSelected
                                }
                                months={previousDateRangePickerProps.months}
                                hoveredDate={
                                    previousDateRangePickerProps.hoveredDate
                                }
                                error={previousDateRangePickerProps.error}
                                setHoveredDate={
                                    previousDateRangePickerProps.setHoveredDate
                                }
                                rangeIsValid={
                                    previousDateRangePickerProps.rangeIsValid
                                }
                                handleFirstMonthChange={
                                    previousDateRangePickerProps.handleFirstMonthChange
                                }
                                handleSecondMonthChange={
                                    previousDateRangePickerProps.handleSecondMonthChange
                                }
                                handleApply={applyDates}
                            />
                        )}
                    </Styled.MultiDateRangePickerWrapper>
                </OnOutsideClick>
            </Portal>
        </Styled.DateRangePickerWrapper>
    );
};

/**
 * The `DateRangePickerField` is a strongly typed `Select`
 * that uses the `DateRangePicker` from `react-final-form`.
 *
 * It prevents us from having to re-type these type definitions
 * whenever we need to use a `DateRangePicker` with a `Field` component.
 */
export function DateRangePickerField(
    props: DateRangePickerFieldProps & { name: string }
): JSX.Element {
    const { name, v2 } = props;
    return (
        // eslint-disable-next-line
        // @ts-ignore
        <Field<string, DateRangePickerFieldComponentProps, HTMLInputElement>
            {...props}
            name={name}
            component={DateRangePicker}
            v2={v2}
        />
    );
}

export default DateRangePicker;
